import { Component, inject, OnInit } from '@angular/core';
import { ClashFilterByHourAndLocal } from '@models/clash/clash-form.dto';
import {
  ClashBaseForm,
  ClashRegistrationFightForm,
} from '@models/clash/clashes/registration-fight-dto';
import { ClassificationsByParameterizationDto } from '@models/competition/dtos/classification/classifications-by-parameterization-dto';
import { RegistrationKeyCompleteDto } from '@models/clash/clashes/registration-key-complete-dto';
import { ClashByHourDto, GamesTableDto } from '@models/embate/games-table-dto';
import { MetadataPutEnum } from '@models/enums/metadata-put-enum';
import { DateTimeEnum } from '@shared/components/date-time-picker/date-time-picker.component';
import { ClassificationService } from '@shared/services/competition/classification/classification.service';
import { ClashRegistrationService } from '@shared/services/competition/clash-registration/clash-registration.service';
import { distinctUntilChanged, finalize, Subject, take, takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/core/utilities/base-component/base-component';
import {
  FilterFormValue,
  FilterGamesTableService,
} from './filter-games-table/services/filter-games-table.service';

type ClassificationAccordionGroup = ClassificationsByParameterizationDto & {
  loaded: boolean;
  loading: boolean;
  minimized: boolean;
};

@Component({
  selector: 'sigecom-games-table',
  templateUrl: './games-table.component.html',
})
export class GamesTableComponent extends BaseComponent implements OnInit {
  DateTimeEnum = DateTimeEnum;
  selectedView: 'jogos' | 'classificacao' = 'jogos';

  modalNewConfrontation: boolean = false;
  modalAddParticipants: boolean = false;
  isEditing: boolean = false;
  enableLoading: boolean = false;
  isAllSelected: boolean = false;

  selectedParticipantA?: RegistrationKeyCompleteDto;
  selectedParticipantB?: RegistrationKeyCompleteDto;

  gameTableEdit?: GamesTableDto;
  clashByHourEdit?: ClashByHourDto;

  selectedSide: 'A' | 'B' | null = null;

  participantsAdded: RegistrationKeyCompleteDto[] = [];
  participantsForModal: RegistrationKeyCompleteDto[] = [];

  embNrId?: number;

  private destroy$ = new Subject<void>();

  comNrId!: string;

  gamesTable: GamesTableDto[] = [];
  classificationGroups: ClassificationAccordionGroup[] = [];
  filterValue!: FilterFormValue;
  petNrId: number | null = null;

  public filtroGamesTableService = inject(FilterGamesTableService);
  private clashRegistrationService = inject(ClashRegistrationService);
  private classificationService = inject(ClassificationService);

  ngOnInit(): void {
    this.filterValue = this.filtroGamesTableService.getCurrentFilterFormValue();
    this.listenChangesIndependentFilter();
    this.listenChangesFilter();
    this.gamesTable = [];
  }

  private listenChangesIndependentFilter() {
    this.filtroGamesTableService.filterIndepedentFormValue$
      .pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (value) => {
          this.isAllSelected = this.verifyAllFiltersSelected(value);
        },
      });
  }

  private listenChangesFilter() {
    this.filtroGamesTableService.filterFormValue$
      .pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (value) => {
          this.filterValue = value!;
          if (
            this.filtroGamesTableService.selectedArena() !== undefined ||
            value?.fase != null ||
            value?.competicao != null
          ) {
            this.loadGamesTableByFilter(value!);
          } else {
            this.gamesTable = [];
          }
          if (this.selectedView === 'classificacao') {
            this.loadClassificationGroupsByFilter(value!);
          }
          this.isAllSelected = this.verifyAllFiltersSelected(value!);
        },
      });
  }

  listenFilterSaved(filter: FilterFormValue) {
    this.loadGamesTableByFilter(filter);
    if (this.selectedView === 'classificacao') {
      this.loadClassificationGroupsByFilter(filter);
    }
  }

  selectView(view: 'jogos' | 'classificacao') {
    this.selectedView = view;
    if (view === 'classificacao') {
      this.loadClassificationGroupsByFilter(this.filterValue);
    }
  }

  loadGamesTableByFilter(filter: FilterFormValue) {
    if (this.isEditing) return;
    const clashFilter = this.builderFilterGamesTableForm(filter);
    this.clashRegistrationService
      .getClashsByHourAndLocal(clashFilter)
      .pipe(
        take(1),
        this.takeUntilDestroy(),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe({
        next: (response) => {
          this.gamesTable = response ?? [];
        },
      });
  }

  loadClassificationGroupsByFilter(filter: FilterFormValue) {
    const petNrId = filter?.fase ?? undefined;

    if (!petNrId) {
      this.classificationGroups = [];
      return;
    }

    const currentGroup = this.classificationGroups.find(
      (group) => group.petNrId === petNrId,
    );

    if (currentGroup) {
      this.classificationGroups = [currentGroup];
      return;
    }

    const fase = this.filtroGamesTableService
      .getCurrentOptionsFilterFormValue()
      .faseOptions?.content?.find((item) => item.petNrId === petNrId);

    this.classificationGroups = [
      {
        petNrId,
        petTxNome: fase?.petTxNome ?? `Parametrização ${petNrId}`,
        catTxSexo: fase?.catTxSexo ?? '',
        eveTxNome: fase?.eveTxNome ?? '',
        chaves: [],
        loaded: false,
        loading: false,
        minimized: true,
      },
    ];
  }

  toggleClassificationGroup(group: ClassificationAccordionGroup) {
    const shouldLoad = group.minimized && !group.loaded && !group.loading;
    group.minimized = !group.minimized;

    if (!shouldLoad) {
      return;
    }

    this.loadClassificationsByParameterization(group.petNrId);
  }

  private loadClassificationsByParameterization(petNrId: number) {
    this.updateClassificationGroup(petNrId, { loading: true });

    this.classificationService
      .searchClassificationsByParameterization({ petNrId })
      .pipe(
        take(1),
        this.takeUntilDestroy(),
        finalize(() =>
          this.updateClassificationGroup(petNrId, { loading: false }),
        ),
      )
      .subscribe({
        next: (response) => {
          const group = response.response?.find(
            (item) => item.petNrId === petNrId,
          );

          this.updateClassificationGroup(petNrId, {
            ...group,
            loaded: true,
            loading: false,
          });
        },
        error: (error) => this.messageErrorResolver.resolveMessage(error),
      });
  }

  private updateClassificationGroup(
    petNrId: number,
    data: Partial<ClassificationAccordionGroup>,
  ) {
    this.classificationGroups = this.classificationGroups.map((group) =>
      group.petNrId === petNrId ? { ...group, ...data } : group,
    );
  }

  builderFilterGamesTableForm(
    filter: FilterFormValue,
  ): ClashFilterByHourAndLocal {
    const clashFilter: ClashFilterByHourAndLocal = {
      petNrId: filter.fase ?? undefined,
      cheNrId: filter.chave ?? undefined,
      areNrId: this.filtroGamesTableService.selectedArena()?.areNrId!,
      epcNrId: filter.espaco ?? undefined,
      comNrId: filter.competicao ?? undefined,
      embDtEmbate: filter.competitionDate ?? undefined,
      embTmEmbate: filter.competitionHour ?? undefined,
    };

    return clashFilter;
  }
  editConfrontationParticipants(clashByHourDto: ClashByHourDto) {
    this.isEditing = true;
    this.embNrId = clashByHourDto.embNrId;
    this.selectedParticipantA = clashByHourDto.embListInscricoes[0]?.embateDto;
    this.selectedParticipantB = clashByHourDto.embListInscricoes[1]?.embateDto;
    this.clashByHourEdit = clashByHourDto;
    this.filtroGamesTableService.updateEditFight(this.isEditing);
    this.openModalNewConfrontation();
  }

  editGamesTableParticipants(gamesTable: GamesTableDto) {
    this.gameTableEdit = gamesTable;
  }

  openModalNewConfrontation() {
    this.modalNewConfrontation = true;
  }

  closeModalNewConfrontation() {
    this.modalNewConfrontation = false;
    this.isEditing = false;
    this.selectedParticipantA = undefined;
    this.selectedParticipantB = undefined;
    this.embNrId = undefined;
    this.filtroGamesTableService.updateEditFight(this.isEditing);
    this.gameTableEdit = undefined;
    this.clashByHourEdit = undefined;

    this.isAllSelected = this.verifyAllFiltersSelected(this.getCurrentFilter());

    if (
      this.filtroGamesTableService.selectedArena() !== undefined ||
      this.filterValue?.fase != null ||
      this.filterValue?.competicao != null
    ) {
      this.loadGamesTableByFilter(
        this.filtroGamesTableService.getCurrentFilterFormValue(),
      );
    } else {
      this.gamesTable = [];
    }
  }

  openModalAddParticipants(side: 'A' | 'B') {
    this.participantsForModal = [];

    if (this.selectedParticipantA) {
      this.participantsForModal.push(this.selectedParticipantA);
    }

    if (this.selectedParticipantB) {
      this.participantsForModal.push(this.selectedParticipantB);
    }

    this.selectedSide = side;
    this.modalAddParticipants = true;
  }

  openModalViewParticipants(valor: boolean) {}

  closeModalAddParticipants() {
    this.modalAddParticipants = false;
  }

  participantAdded(participant: RegistrationKeyCompleteDto) {
    if (this.selectedSide === 'A') {
      this.selectedParticipantA = participant;
    } else if (this.selectedSide === 'B') {
      this.selectedParticipantB = participant;
    }

    this.closeModalAddParticipants();
  }

  saveConfrontation() {
    if (this.enableLoading) return;

    this.enableLoading = true;

    const subscriptions: ClashRegistrationFightForm[] = [];

    if (this.selectedParticipantA) {
      const formParticipantA = this.buildFormParticipantSubmit(
        this.selectedParticipantA,
      );
      subscriptions.push(formParticipantA);
    }
    if (this.selectedParticipantB) {
      const formParticipantB = this.buildFormParticipantSubmit(
        this.selectedParticipantB,
      );
      subscriptions.push(formParticipantB);
    }

    if (subscriptions.length > 0) {
      const form = this.buildClashBaseForm(subscriptions);

      if (!this.isEditing) {
        this.clashRegistrationService.saveRegistraionClash(form).subscribe({
          next: () => {
            this.snackbarService.showSuccess('Confronto Salvo com Sucesso!');
            this.loadGamesTableByFilter(this.filterValue);
            this.closeModalNewConfrontation();
          },
          error: (error) => this.messageErrorResolver.resolveMessage(error),
          complete: () => (this.enableLoading = false),
        });
      } else {
        this.clashRegistrationService.updateRegistraionClash(form).subscribe({
          next: () => {
            this.snackbarService.showSuccess('Confronto Editado com Sucesso!');
            this.loadGamesTableByFilter(this.filterValue);
            this.closeModalNewConfrontation();
          },
          error: (error) => this.messageErrorResolver.resolveMessage(error),
          complete: () => (this.enableLoading = false),
        });
      }
    }
  }

  refreshGameTable() {
    this.loadGamesTableByFilter(this.filterValue);
  }

  buildFormParticipantSubmit(participant: RegistrationKeyCompleteDto) {
    const clashForm: ClashRegistrationFightForm = {
      entNrId: !this.embNrId ? undefined : this.embNrId,
      ichNrId: participant.ichNrId,
      embNrIdOrigem: undefined,
      iemTxNome: '',
      iemNrVencedor: undefined,
      iemBlSorteio: false,
    };

    return clashForm;
  }

  buildClashBaseForm(subscriptions: ClashRegistrationFightForm[]) {
    const filter = this.getCurrentFilter();

    const dateHourString = `${filter.competitionDate}T${filter.competitionHour}`;

    // ============================================================================================
    //                          MARCOS NAO ESQUEÇA DE AJUSTAR O RODNRID
    // ============================================================================================

    const clashBaseForm: ClashBaseForm = {
      entNrId: !this.embNrId ? undefined : this.embNrId,
      cheNrId: filter.chave!,
      embDtDataHoraEmbate: dateHourString,
      epcNrId: filter.espaco!,
      rodNrId: 0,
      petNrId: filter.fase!,
      inscricaoEmbateFormulario: subscriptions,
      entTxMetadado: !this.embNrId
        ? MetadataPutEnum.CRIADO
        : MetadataPutEnum.ALTERADO,
    };

    return clashBaseForm;
  }

  getCurrentFilter() {
    const filter = !this.isEditing
      ? this.filterValue
      : this.filtroGamesTableService.getCurrentFilterIndependetFormValue();

    return filter;
  }

  verifyAllFiltersSelected(value: FilterFormValue) {
    return (
      this.filtroGamesTableService.selectedArena() !== null &&
      value.espaco !== null &&
      value.competitionDate !== null &&
      value.competitionHour !== null
    );
  }

  trackClassificationGroupByPetNrId(
    _: number,
    group: ClassificationAccordionGroup,
  ): number {
    return group.petNrId;
  }
}
